<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('globalUsersTable.title') }}
    </v-toolbar>
    <v-data-table
        :headers="headers"
        :items="items"
        :server-items-length="total"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100, 200] }"
        :loading="loading"
        class="elevation-12"
    >
      <template v-slot:top>
        <v-sheet color="grey lighten-3"
                 class="px-4 pt-4 pb-3"
                 tile>
          <h3 class="transition-swing font-weight-medium">{{ $t('globalUsersTable.filterTitle') }}</h3>
          <v-responsive>
            <v-row>
              <v-col cols="12" sm="6" md="3" style="padding-top: 16px;">
                <v-autocomplete v-model="filters.unitId"
                                :items="units"
                                :label="$t('common.labels.department') + 's'"
                                :dense="true"
                                :clearable="true"
                                item-text="name"
                                item-value="id"
                                @change="onFiltersChanged"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="3" class="">
                <v-text-field
                    :label="$t('common.labels.identifier')"
                    v-model="filters.user"
                    :clearable="true"
                    @change="onFiltersChanged"
                    class="pt-0"
                />
              </v-col>
              <v-spacer/>
              <v-col cols="12" sm="12" md="12" class="d-flex justify-space-between">
                <v-btn color="primary" @click="updateItems">
                  {{ $t('common.actions.update') }}
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
                <v-btn color="primary" @click="onAddUserButtonClicked">{{ $t('globalUsersTable.addUserBtn') }}</v-btn>
                <v-dialog v-model="addUserDialog" :persistent="true" width="600px">
                  <AddGlobalUserComponent
                      :inn="filters.inn"
                      @cancel="onAddUserCanceled"
                      @added="onAddUserSuccess"
                      ref="addAdminUserComponent"/>
                </v-dialog>
              </v-col>
            </v-row>
          </v-responsive>
        </v-sheet>
      </template>

      <template v-slot:item.globalorganization.createdOn="{item}">
        {{ item.globalorganization.createdOn.format('DD.MM.YYYY HH:mm:ss') }}
      </template>

      <template v-slot:item.name="{item}">
        <template v-if="item.isAdmin">
          <v-badge
              color="error"
              :content="$t('userTypes.admin')"
          >
            {{ item.name }}
          </v-badge>
        </template>
        <template v-else-if="item.isModerator">
          <v-badge
              color="success"
              :content="$t('userTypes.controller')"
          >
            {{ item.name }}
          </v-badge>
        </template>
        <template v-else-if="item.isMonitoring">
          <v-badge
              color="info"
              :content="$t('userTypes.monitoring')"
          >
            {{ item.name }}
          </v-badge>
        </template>
        <template v-else-if="item.type === 5">
          <v-badge
              color="warning"
              :content="$t('userTypes.auditor')"
          >
            {{ item.name }}
          </v-badge>
        </template>
        <template v-else>
          {{ item.name }}
        </template>
      </template>

      <template v-slot:item.actions="{item}">
        <div class="d-flex justify-end">
          <v-btn v-if="$store.getters.appLogin.globalunit.isHead" icon @click="onEditPermissionButtonClicked(item)">
            <v-icon>mdi-key</v-icon>
          </v-btn>
          <v-btn icon @click="onEditUserButtonClicked(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="editUserDialog" :persistent="true" width="600px">
      <EditGlobalUserComponent ref="editUser" @cancel="onEditUserCanceled" @updated="onEditUserSuccess" />
    </v-dialog>

    <v-dialog v-model="editPermissionDialog" :persistent="true" width="1300px">
      <EditGlobalUserUnitPermission ref="editPermission" @cancel="onEditPermissionCanceled" @updated="onEditPermissionSuccess" />
    </v-dialog>

    <v-snackbar v-model="isAddUserSuccess"
                :timeout="5000"
                :top="true"
                color="success"
    >
      {{ $t('globalUsersTable.messages.addSuccess') }}
    </v-snackbar>

    <v-snackbar v-model="isEditUserSuccess"
                :timeout="5000"
                :top="true"
                color="success"
    >
      {{ $t('globalUsersTable.messages.editSuccess') }}
    </v-snackbar>
  </v-card>
</template>


<script>
import _ from "underscore";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import Mappers from "../../mappers";
import EditGlobalUserComponent from "@/components/user/EditGlobalUserComponent.vue";
import AddGlobalUserComponent from "@/components/user/AddGlobalUserComponent.vue";
import GlobalUnitService from "@/services/global.unit.service";
import EditGlobalUserUnitPermission from "@/components/user/EditGlobalUserUnitPermission.vue";
import user from "@/domain/User";

export default {
  name: "GlobalUsersDataTable",
  props: {
    users: {
      type: Array,
      default: () => {
        return []
      }
    },
    total: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  components: {
    EditGlobalUserUnitPermission,
    AddGlobalUserComponent,
    EditGlobalUserComponent,
  },
  data() {
    return {
      units: [],

      options: {
        page: 1,
        itemsPerPage: 50,
      },
      filters: {
        inn: null,
        unitId: null,
        user: null
      },

      addUserDialog: false,
      editUserDialog: false,
      editPermissionDialog: false,
      isAddUserSuccess: false,
      isEditUserSuccess: false,
      isAddPermissionSuccess: false,
      isEditPermissionSuccess: false
    }
  },
  computed: {
    user() {
      return user
    },
    headers() {
      const headers = []

      headers.push({
        text: this.$t('globalUsersTable.headers.number'),
        align: 'center',
        sortable: false,
        value: 'displayNumber'
      })

      headers.push({
        text: this.$t('common.labels.fullName'),
        align: 'left',
        sortable: true,
        value: 'name'
      })

      headers.push({
        text: this.$t('common.labels.login'),
        align: 'left',
        sortable: true,
        value: 'username'
      })

      headers.push({
        text: this.$t('common.labels.organization'),
        align: 'left',
        sortable: true,
        value: 'globalorganization.name'
      })

      headers.push({
        text: this.$t('globalUsersTable.headers.tin'),
        align: 'center',
        sortable: true,
        value: 'globalorganization.inn'
      })

      headers.push({
        text: this.$t('globalUsersTable.headers.address'),
        align: 'left',
        sortable: true,
        value: 'globalorganization.address'
      })

      headers.push({
        text: this.$t('common.labels.department'),
        align: 'center',
        sortable: true,
        value: 'globalunit.name'
      })

      headers.push({
        text: this.$t('globalUsersTable.headers.orgRegistrationDate'),
        align: 'center',
        sortable: true,
        value: 'globalorganization.createdOn'
      })

      headers.push({
        text: '',
        align: 'right',
        sortable: false,
        value: 'actions'
      })

      return headers
    },
    items() {
      let number = 1;
      let skip = this.skip;
      return _.map(this.users, u => {
        u.displayNumber = skip + (number++)
        return u
      })

    },
    skip() {
      return (this.options.page - 1) * this.options.itemsPerPage
    }
  },
  methods: {
    updateItems() {
      this.$emit("update", this.options, this.filters)
    },

    updateUnitPermissions(){
      this.$emit("update-unit-permissions", this.options, this.filters)
    },

    onFiltersChanged() {
      if (this.options.page !== 1)
        this.options.page = 1;
      else
        this.updateItems();
    },
    onAddUserButtonClicked() {
      this.addUserDialog = true
      this.$nextTick(() => {
        this.$refs.addAdminUserComponent.init()
      })
    },
    onAddUserCanceled() {
      this.addUserDialog = false
    },
    onAddUserSuccess() {
      this.isAddUserSuccess = true
      this.addUserDialog = false
      this.updateItems()
    },
    onEditUserButtonClicked(user) {
      this.editUserDialog = true

      this.$nextTick(() => {
        this.$refs.editUser.set(user)
      })
    },
    onEditPermissionButtonClicked(user) {
      this.editPermissionDialog = true

      this.$nextTick(() => {
        this.$refs.editPermission.set(user)
      })
    },
    onEditPermissionCanceled() {
      this.editPermissionDialog = false
    },
    onEditPermissionSuccess() {
      this.isEditPermissionSuccess = true
      this.editPermissionDialog = false
      this.updateItems()
    },
    onEditUserCanceled() {
      this.editUserDialog = false
    },
    onEditUserSuccess() {
      this.isEditUserSuccess = true
      this.editUserDialog = false
      this.updateItems()
    },
    loadUnits() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      var inn = this.filters.inn
      GlobalUnitService.getUnits({inn})
          .then(response => {
            this.units = _.map(response.data, o => {
              return Mappers.mapGlobalUnit(o)
            })
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
  },
  created() {
    this.loadUnits()

    if (this.$route.query.inn)
      this.filters.unitId = this.$route.query.unit

    // this.updateItems()
  },
  watch: {
    options: {
      handler() {
        this.updateItems()
      }
    }
  }
}
</script>

<style scoped>

</style>